import React from 'react';
import { Plus } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * Mobile FAB (Floating Action Button) Component
 * 
 * Positioned above the bottom navigation bar with proper safe-area handling.
 * Uses smaller size on mobile with bolder plus icon.
 * 
 * Usage:
 * <MobileFAB onClick={() => {}} />
 * <MobileFAB onClick={() => {}} disabled />
 * <MobileFAB onClick={() => {}} rotating />
 */
export const MobileFAB = ({ 
  onClick, 
  disabled = false, 
  rotating = false,
  className,
  children,
  'data-testid': dataTestId = 'mobile-fab'
}) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={dataTestId}
      className={cn(
        // Base styles
        "md:hidden fixed z-[55] w-14 h-14 rounded-full shadow-lg flex items-center justify-center",
        // Smooth press animation
        "transition-transform duration-150 active:scale-90",
        // Position: above bottom nav (h-16 = 64px) + safe area + generous margin
        "bottom-[calc(5.5rem+env(safe-area-inset-bottom))] right-4",
        // Disabled state
        disabled 
          ? "bg-muted text-muted-foreground cursor-not-allowed" 
          : "bg-primary text-primary-foreground hover:bg-primary/90",
        className
      )}
    >
      {children || (
        <Plus 
          className={cn(
            "w-6 h-6 transition-transform",
            rotating && "rotate-45"
          )} 
          strokeWidth={2.5}
        />
      )}
    </button>
  );
};

/**
 * Mobile FAB Container
 * 
 * Use this to wrap FAB with menu options.
 * Positions the menu above the FAB.
 */
export const MobileFABContainer = ({ 
  children, 
  className,
  'data-testid': dataTestId = 'mobile-fab-container'
}) => {
  return (
    <div 
      className={cn(
        "md:hidden fixed z-[55]",
        // Position: above bottom nav with safe area
        "bottom-[calc(5.5rem+env(safe-area-inset-bottom))] right-4",
        className
      )}
      data-testid={dataTestId}
    >
      {children}
    </div>
  );
};

/**
 * Mobile FAB Menu
 * 
 * Dropdown menu that appears above the FAB.
 */
export const MobileFABMenu = ({ 
  open, 
  onClose, 
  children,
  className 
}) => {
  if (!open) return null;
  
  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/20 z-40" 
        onClick={onClose}
      />
      {/* Menu */}
      <div 
        className={cn(
          "absolute bottom-14 right-0 bg-background border rounded-lg shadow-lg py-2 min-w-[160px] z-50",
          "animate-in fade-in-0 zoom-in-95 origin-bottom-right duration-150",
          className
        )}
      >
        {children}
      </div>
    </>
  );
};

/**
 * Mobile FAB Menu Item
 */
export const MobileFABMenuItem = ({ 
  onClick, 
  icon: Icon, 
  children,
  className,
  'data-testid': dataTestId
}) => {
  return (
    <button
      className={cn(
        "w-full px-4 py-2.5 text-left text-sm font-medium hover:bg-muted flex items-center gap-2",
        className
      )}
      onClick={onClick}
      data-testid={dataTestId}
    >
      {Icon && <Icon className="w-4 h-4" />}
      {children}
    </button>
  );
};

export default MobileFAB;
